/* <cart-addons-rail> — the cart drawer add-ons carousel ("attach shelf").

   Design: docs/cart-upsell-design-guide.md. Markup: snippets/cart-drawer-addons
   .liquid. This file owns four things the markup cannot: surviving the drawer
   re-render with its scroll position intact, the desktop prev/next arrows,
   adding to cart, and analytics.

   ES5 only, no build step, no framework — matching assets/fb-posthog-commerce.js
   and assets/gift-card-upsell-prompt.js. */
(function () {
  if (customElements.get('cart-addons-rail')) return;

  /* The four drawer sections the Section Rendering API repaints after a cart
     mutation. Hardcoded in four other places already (gift-card-upsell-prompt
     .js:8-13, cart-notification.js:236-241, :515-520, delivery-suburb-popup
     .js:345-350); if a fifth drawer section is ever added it must be added to
     all of them. */
  var DRAWER_SECTIONS = [
    'cart-notification-product',
    'cart-notification-subtotal',
    'cart-notification-count',
    'cart-notification-discount'
  ];

  /* THE SCROLL LATCH.

     The rail is rendered inside #cart-notification-product, which the drawer
     replaces wholesale with innerHTML on every add, quantity change and removal
     (cart-notification.js:617-619). That destroys the element and resets
     scrollLeft to 0 — so a shopper who scrolled to the Edit items and tapped Add
     would be thrown back to the start of the rail, which is exactly the
     behaviour the deep-scroll design depends on not happening.

     The element is re-created on every repaint; this module is not. Holding the
     position out here and restoring it in connectedCallback survives every
     render path — including the two that bypass CartNotification.renderContents
     — without patching any of them. */
  var lastScrollLeft = 0;

  /* Impression latches. One MutationObserver-driven repaint fires several times
     per innerHTML assignment (fb-posthog-commerce.js:576-578), and the drawer
     re-renders on every cart change, so both of these events would otherwise
     fire many times per drawer open. Reset when the drawer closes. */
  var viewedThisOpen = false;
  var deepestPositionReported = 0;

  /* Identifies ONE drawer open, so an impression, a scroll and an add can be
     joined to each other. Module scope for the same reason as the latches
     above: the Section Rendering API repaint destroys and rebuilds the rail
     on every cart change, and an id held on the element would be reminted
     mid-open, splitting one impression into several. Minted on open, cleared
     on close. ES5, so no crypto.randomUUID. */
  var openId = null;
  var openCounter = 0;

  function mintOpenId() {
    openCounter += 1;
    return openCounter + '-' + Math.random().toString(36).slice(2, 10);
  }

  /* Module scope, not instance state. Every cart mutation destroys the rail and
     builds a new one, so an in-flight flag held on the element would be reset
     by the very repaint the previous add triggered — and the guard would not
     guard anything. There is no lock, queue or AbortController anywhere in the
     drawer's cart plumbing, so two adds landing out of order would let the
     staler section HTML win. */
  var addInFlight = false;

  /* --------------------------------------------------------- slot resolution */

  var HIDDEN_CLASS = 'fbr-hidden';
  var POSTCODE_COOKIE = 'deliveryPostcode';

  /* Same five as the rest of the codebase (fb-variant-location-filter.js,
     fb-suburb-grid.js, delivery-suburb-popup.js). Only a fallback:
     window.fbLocationId is authoritative and covers a new city with no code
     change here. */
  var STATIC_LOCATION_IDS = {
    Brisbane: '70541672681',
    Melbourne: '70507725033',
    Sydney: '70541639913',
    RestofAustralia: '74290069737',
    Adelaide: '90760315113'
  };

  function getCookie(name) {
    var parts = ('; ' + document.cookie).split('; ' + name + '=');
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  }

  /* window.fbResolvePostcode has TWO failure modes and only one is falsy:
       null               -> no app config on the page at all
       'Unknown Location' -> a complete config that matched nothing
     The string is truthy; treating it as a location cost a real bug in the cart
     extension on 1 Aug 2026. Both mean "we cannot place this shopper", and this
     rail then leaves the server's ranking exactly as rendered.

     window.findPostcodeLocation is deliberately NOT a fallback: with its
     postcode arrays missing it returns a hardcoded "Sydney"
     (delivery-suburb-popup.js:1394), and guessing a city would swap a shopper
     onto a different product for no reason. */
  function resolveLocationId() {
    var postcode = getCookie(POSTCODE_COOKIE);
    if (!postcode) return null;
    if (typeof window.fbResolvePostcode !== 'function') return null;

    var location = window.fbResolvePostcode(postcode);
    if (!location || location === 'Unknown Location') return null;

    var id = null;
    if (typeof window.fbLocationId === 'function') id = window.fbLocationId(location);
    id = id || STATIC_LOCATION_IDS[location] || null;
    return id ? String(id) : null;
  }

  /* Mirrors the add-to-cart gate at assets/product-form.js:773-777 exactly:
     block only when the product manages locations at all AND declares this one
     as something other than true. A product with no flag for this location is
     NOT blocked — the client's decision of 1 Aug 2026, after "missing means
     unavailable" was found to hide 152 products in Adelaide alone.

     Mirroring matters in both directions. Looser than the gate and the rail
     offers a card that add-to-cart then refuses; stricter and it hides stock
     that would have sold. */
  function deliverableTo(card, locationId) {
    if (!locationId) return true;
    if (card.getAttribute('data-loc-managed') !== 'true') return true;
    var flag = card.getAttribute('data-avail-' + locationId);
    if (flag === null) return true;
    return flag === 'true';
  }

  /* ------------------------------------------------------------- analytics */

  /* Shopify's visitor token, reproduced rather than invented so rail events join
     against the pixel-parity events (fb-posthog-commerce.js:88-92). */
  function clientId() {
    var match = document.cookie.match(/(?:^|;\s*)_shopify_y=([^;]*)/);
    return match ? decodeURIComponent(match[1]) : null;
  }

  /* Theme-native events use the snake_case register. The Title Case names
     ('Product Added' and friends) are frozen pixel replicas and must not be
     extended — see the header comment in assets/fb-posthog-commerce.js:28-47. */
  function capture(event, props) {
    try {
      if (!window.posthog || typeof window.posthog.capture !== 'function') return;
      var payload = props || {};
      var client = clientId();
      if (client) payload.client_id = client;
      /* Stamped centrally rather than at each call site so no rail event can
         be added later without it. Without an open id the three rail events
         can only be grouped by session plus timestamp, which makes "did the
         shopper who saw this add this" an inference rather than a join — and
         a session spans many drawer opens. */
      if (openId) payload.open_id = openId;
      window.posthog.capture(event, payload);
    } catch (err) {
      /* Analytics must never throw into the rail's own behaviour. */
    }
  }

  function dollars(cents) {
    return Math.round(cents) / 100;
  }

  /* dollars() for a value read off a data attribute, which arrives as a string
     and is absent entirely on a render that predates the attribute. Math.round
     would turn that into NaN, and NaN serialises to null in the capture
     payload anyway — but only by accident, and an unparseable value would
     silently read as "no cart". Be explicit instead. */
  function dollarsFromAttr(raw) {
    if (raw === '' || raw === undefined || raw === null) return null;
    var cents = Number(raw);
    return isFinite(cents) ? dollars(cents) : null;
  }

  /* cart.js and Liquid disagree about '' vs null for absent strings; normalise
     so PostHog does not end up with both as distinct property values
     (fb-posthog-commerce.js:96-98). */
  function orNull(value) {
    return value === '' || value === undefined ? null : value;
  }

  /* .active is added by open() and removed by close(), both on the
     <cart-notification> element itself (id offcanvas__mini_cart). */
  function drawerIsOpen() {
    var drawer = document.querySelector('cart-notification');
    return !!drawer && drawer.classList.contains('active');
  }

  function slotPayload(slot) {
    var node = slot.querySelector('[data-fbr-payload]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (err) {
      return null;
    }
  }

  /* The arrows ship `disabled` and the CSS renders disabled as hidden, so
     enabling one is what shows it. The attribute rather than a class so the
     button also leaves the tab order and stops taking clicks for free. */
  function setLive(button, live) {
    if (live) {
      button.removeAttribute('disabled');
    } else {
      button.setAttribute('disabled', 'disabled');
    }
  }

  customElements.define('cart-addons-rail', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-fbr-track]');
      if (!this.track || this._bound) return;
      this._bound = true;

      /* Found before resolveSlots(), which refreshes them. Either can be
         missing (older cached markup, a stripped-down render) and everything
         below tolerates that: the rail still scrolls, it just has no arrows. */
      this.prev = this.querySelector('[data-fbr-nav="prev"]');
      this.next = this.querySelector('[data-fbr-nav="next"]');

      /* Before anything measures or reports, settle which candidate owns each
         slot. Runs first so the scroll restore and the impression both see the
         rail the shopper will actually see. */
      this.resolveSlots();

      /* Restore before the browser paints, so the rail never flashes at
         position 0 after a cart update.

         Must be an explicit instant scroll: the track sets
         `scroll-behavior: smooth` for snap, and per CSSOM-View a plain
         scrollLeft assignment inherits that, which would animate the restore
         from 0 — producing exactly the flash this latch exists to prevent. */
      if (lastScrollLeft > 0) {
        if (typeof this.track.scrollTo === 'function') {
          this.track.scrollTo({ left: lastScrollLeft, behavior: 'instant' });
        } else {
          this.track.scrollLeft = lastScrollLeft;
        }
      }

      this.updateNav();

      this._onScroll = this.onScroll.bind(this);
      this._onClick = this.onClick.bind(this);
      this.track.addEventListener('scroll', this._onScroll, { passive: true });

      /* The drawer is 380px until 992px and 420px after, and the cards change
         width at the same breakpoint (cart-drawer-addons.css), so a resize
         across it changes whether the rail overflows at all. */
      this._onResize = this.updateNav.bind(this);
      window.addEventListener('resize', this._onResize);

      /* Delegated, so it keeps working for cards added by a later re-render and
         cannot leak a listener per card (fb-posthog-commerce.js:617-632).

         CAPTURE PHASE, and this is load-bearing. CartNotification's constructor
         binds close() to EVERY button[type="button"] inside the drawer
         (cart-notification.js:558-560) — a blanket selector that matches this
         rail's Add button, so a tap would add the item and slam the drawer shut
         in the same gesture. Listening during capture on the rail lets
         onClick's stopPropagation() halt the event before it reaches the
         button's own close handler.

         Narrowing that selector in cart-notification.js would be the better
         long-term fix, but it also governs the event-removal modal's buttons,
         so it is not this feature's change to make. */
      this.addEventListener('click', this._onClick, true);

      /* Only counts if the drawer is actually open. The drawer markup is
         rendered into every page, so connectedCallback fires on page load with
         the drawer shut — reporting here unconditionally would inflate the
         impression denominator with views nobody saw. When it is shut, the
         open-watcher below reports instead. */
      this.reportView();
    }

    disconnectedCallback() {
      if (this.track && this._onScroll) {
        this.track.removeEventListener('scroll', this._onScroll);
      }
      /* The `true` must match the capture flag used to add it, or this does not
         detach the listener at all. */
      if (this._onClick) this.removeEventListener('click', this._onClick, true);
      if (this._onResize) window.removeEventListener('resize', this._onResize);
      this._bound = false;
    }

    /* Every candidate card in the rail, across all slots. */
    cards() {
      return Array.prototype.slice.call(this.querySelectorAll('[data-fbr-slot]'));
    }

    /* The slot groups, in rail order. */
    groups() {
      return Array.prototype.slice.call(this.querySelectorAll('[data-fbr-slot-group]'));
    }

    /* The cards actually on screen — one per slot that resolved. This is what
       "the rail" means for analytics: the shopper never sees the candidates
       waiting behind each winner. */
    slots() {
      return this.cards().filter(function (card) {
        return !card.classList.contains(HIDDEN_CLASS);
      });
    }

    /* Pick one winner per slot: the highest-ranked candidate deliverable to
       this shopper. Rank is DOM order, which is the product's position in its
       slot collection.

       Re-runs from scratch every time rather than only ever narrowing, so a
       shopper who changes their suburb from Perth back to Melbourne gets the
       rank-1 product back instead of staying on the fallback. Same reset-first
       discipline as fb-variant-location-filter.js:105-112. */
    resolveSlots() {
      var locationId = resolveLocationId();

      this.groups().forEach(function (group) {
        var candidates = Array.prototype.slice.call(group.querySelectorAll('[data-fbr-slot]'));
        if (!candidates.length) return;

        var winner = null;
        candidates.forEach(function (card) {
          if (!winner && deliverableTo(card, locationId)) winner = card;
        });

        candidates.forEach(function (card) {
          card.classList.toggle(HIDDEN_CLASS, card !== winner);
        });

        /* No candidate in this slot ships to the shopper's territory. Drop the
           slot rather than show something add-to-cart would refuse — the same
           call fb-variant-location-filter.js makes for the upgrade banner when
           no larger size is deliverable. */
        group.classList.toggle(HIDDEN_CLASS, winner === null);
      });

      /* Hiding or revealing cards changes the rail's length, and with it
         whether there is anything to scroll to. */
      this.updateNav();
    }

    /* THE ARROWS.

       The track hides its scrollbar (cart-drawer-addons.css). On touch that
       costs nothing: the peeking card invites a swipe. A mouse has no swipe,
       no bar to drag, and shift+wheel is not a gesture shoppers know, so
       fine-pointer devices get prev/next arrows. The CSS decides who sees
       them; this decides which of them are live.

       An arrow is live only while the rail can move that way. A rail that
       fits its viewport shows none, one at its start only "next", one at its
       end only "prev". Re-run on scroll, resize, drawer open and after slot
       resolution, since each of those can change the answer. */
    updateNav() {
      if (!this.track || !this.prev || !this.next) return;

      var max = this.track.scrollWidth - this.track.clientWidth;
      var left = this.track.scrollLeft;
      /* Sub-pixel scroll positions make exact comparisons flap at the ends. */
      var overflows = max > 1;

      setLive(this.prev, overflows && left > 1);
      setLive(this.next, overflows && left < max - 1);
    }

    /* One page is the number of whole cards the track can show, measured
       rather than assumed: card width and drawer width both change at the
       992px breakpoint (cart-drawer-addons.css). The pitch (card plus gap)
       comes from the first two visible cards and the gutter from where the
       first card sits against the track, so nothing here restates the CSS.
       Both are read at the same instant as scrollLeft, which keeps them right
       mid-animation. Returns null when there is nothing to measure. */
    pageGeometry() {
      var slots = this.slots();
      if (!slots.length) return null;

      var trackRect = this.track.getBoundingClientRect();
      var width = trackRect.width || this.track.clientWidth || 0;
      var first = slots[0].getBoundingClientRect();
      var pitch = first.width || 0;
      if (slots.length > 1) {
        var second = slots[1].getBoundingClientRect();
        if (second.left > first.left) pitch = second.left - first.left;
      }
      if (!pitch || !width) return null;

      var gutter = Math.max(0, first.left - trackRect.left + this.track.scrollLeft);
      var gap = pitch - (first.width || pitch);
      var perPage = Math.max(1, Math.floor((width - gutter * 2 + gap) / pitch));
      return { pitch: pitch, perPage: perPage };
    }

    /* direction: -1 for prev, 1 for next.

       Pages from the snap grid rather than from wherever a smooth scroll
       happens to be mid-flight, so two quick clicks move two pages rather
       than one and a bit. Snap positions sit at multiples of the pitch
       because the track's scroll-padding equals its gutter
       (cart-drawer-addons.css); the end of the rail is the one position that
       is not, and rounding from there still lands the page on the grid. */
    page(direction) {
      if (!this.track) return;
      var geo = this.pageGeometry();
      if (!geo) return;

      var index = Math.round(this.track.scrollLeft / geo.pitch);
      var target = (index + (direction < 0 ? -1 : 1) * geo.perPage) * geo.pitch;

      /* A plain assignment inherits the track's `scroll-behavior: smooth`,
         including the reduced-motion override that sets it back to auto.
         scrollBy({ behavior: 'smooth' }) would force the animation on
         regardless, so it is deliberately not used here. The browser clamps
         the far end. */
      this.track.scrollLeft = Math.max(0, target);
    }

    /* 1-BASED position in the rail as rendered. Derived from DOM order rather
       than counted in Liquid, because any card can be suppressed and a
       Liquid-side count would carry gaps that do not describe what the shopper
       saw. This is the only definition of slot position in the feature. */
    positionOf(slot) {
      return this.slots().indexOf(slot) + 1;
    }

    /* 1-BASED rank of a winning card WITHIN ITS OWN SLOT GROUP: 1 means the
       merchandised first choice won, 2 means it was blocked for this shopper
       and the slot fell through. Derived from DOM order at read time for the
       same reason positionOf is — a rank attribute rendered in Liquid would be
       a second definition of the same number, and they would drift.

       This is the only way a territory fall-through is visible. Without it,
       "the vase slot converts at 0.6%" cannot be separated from "the vase slot
       is showing its second choice to half of Australia". */
    rankOf(slot) {
      var group = slot.closest ? slot.closest('[data-fbr-slot-group]') : null;
      if (!group) return null;
      var candidates = Array.prototype.slice.call(group.querySelectorAll('[data-fbr-slot]'));
      var index = candidates.indexOf(slot);
      return index < 0 ? null : index + 1;
    }

    /* One impression per drawer open, carrying what was actually rendered after
       suppression — which is the denominator for every attach rate the design
       guide asks for.

       Unlike 'Cart Viewed', which deliberately skips the drawer's auto-open
       after an add to cart (fb-posthog-commerce.js:622-624), this DOES count
       auto-opens: an auto-opened drawer is a real chance to attach, and
       excluding it would understate the denominator. */
    reportView() {
      if (viewedThisOpen) return;
      if (!drawerIsOpen()) return;
      viewedThisOpen = true;

      var self = this;
      var slots = this.slots();

      /* One pass, then ONE filter, so the three parallel arrays below stay
         positionally aligned: entry i of slot_handles, slot_ids and
         won_at_rank all describe the same card. Filtering each array
         separately (as this did before won_at_rank existed) silently
         misaligns them the moment one card lacks a payload. */
      var entries = slots.map(function (slot) {
        var payload = slotPayload(slot);
        return {
          handle: payload ? payload.handle : null,
          id: slot.getAttribute('data-fbr-slot-id'),
          rank: self.rankOf(slot)
        };
      }).filter(function (entry) {
        return entry.handle;
      });

      capture('rail_viewed', {
        surface: 'drawer',
        slot_count: slots.length,
        /* Dollars, to match `price` on rail_slot_added — the Liquid emits
           cents. Null rather than 0 when the attribute is missing, so a
           render that predates it cannot masquerade as an empty cart. */
        cart_subtotal: dollarsFromAttr(this.dataset.cartSubtotal),
        /* What won each slot, not what was eligible for it — the losing
           candidates were never on screen. Pairing these tells you whether a
           territory is systematically falling through to second choices. */
        slot_handles: entries.map(function (e) { return e.handle; }),
        slot_ids: entries.map(function (e) { return e.id; }),
        won_at_rank: entries.map(function (e) { return e.rank; }),
        /* Which fulfilment location the rail resolved the shopper to, or null
           when it could not (no cookie, unknown postcode). The rail fails open
           in that case and shows the server's ranking, so a null here is the
           signal that won_at_rank reflects merchandising alone rather than
           merchandising filtered by territory. */
        location_id: orNull(resolveLocationId())
      });
    }

    /* Scroll depth is the revealed-preference signal the zoning rests on: a
       shopper deep in the rail has seen and declined the anchors. Reported as
       the furthest position reached, once, rather than as a stream. */
    onScroll() {
      lastScrollLeft = this.track.scrollLeft;
      this.updateNav();

      var trackRight = this.track.getBoundingClientRect().right;
      var deepest = 0;
      this.slots().forEach(function (slot, index) {
        /* Fully inside the track's viewport, not merely peeking. */
        if (slot.getBoundingClientRect().right <= trackRight + 1) {
          if (index + 1 > deepest) deepest = index + 1;
        }
      });

      if (deepest > deepestPositionReported) {
        deepestPositionReported = deepest;
        if (this._scrollTimer) clearTimeout(this._scrollTimer);
        var self = this;
        /* Settle before reporting so one flick produces one event. */
        this._scrollTimer = setTimeout(function () {
          capture('rail_scrolled', {
            surface: 'drawer',
            deepest_position: deepestPositionReported,
            slot_count: self.slots().length
          });
        }, 400);
      }
    }

    onClick(evt) {
      var target = evt.target && evt.target.closest ? evt.target : null;
      if (!target) return;

      /* The arrows first. They are type="button" inside the drawer for the
         same reason the Add button is, so they carry the same blanket close()
         binding and need the same capture-phase stop. */
      var nav = target.closest('[data-fbr-nav]');
      if (nav && this.contains(nav)) {
        evt.preventDefault();
        evt.stopPropagation();
        this.page(nav.getAttribute('data-fbr-nav') === 'prev' ? -1 : 1);
        return;
      }

      var button = target.closest('[data-fbr-add]');
      if (!button || !this.contains(button)) return;
      evt.preventDefault();
      /* Stops the event reaching the drawer's blanket close() binding on this
         button — see the capture-phase note in connectedCallback. Scoped to our
         own Add buttons so nothing else in the drawer loses its click. */
      evt.stopPropagation();
      this.add(button);
    }

    add(button) {
      /* No queue, lock or AbortController exists anywhere in the drawer's cart
         plumbing (cart-notification.js), so two fast taps would race and the
         later-resolving, possibly stale, section HTML would win. Guard our own
         path at least. */
      if (addInFlight) return;
      addInFlight = true;

      var slot = button.closest('[data-fbr-slot]');
      var payload = slot ? slotPayload(slot) : null;
      /* Read before the add: the repaint that follows replaces every slot. */
      var slotPosition = slot ? this.positionOf(slot) : null;
      var slotRank = slot ? this.rankOf(slot) : null;
      var addLocationId = resolveLocationId();
      /* The repaint is a Section Rendering API fetch, so assets/web-vitals-rum
         .js never sees it and the drawer's slowest interaction is unmeasured.
         Timed here so S1's window doubles as the latency baseline for any
         later change that adds Liquid work to the rail. */
      var addStartedAt = (window.performance && window.performance.now)
        ? window.performance.now()
        : null;
      var variantId = slot ? slot.getAttribute('data-fbr-variant-id') : null;
      if (!variantId) {
        addInFlight = false;
        return;
      }

      var addUrl = this.dataset.addUrl || '/cart/add';
      var cartUrl = this.dataset.cartUrl || '/cart';
      var addedLabel = button.getAttribute('data-added-label') || 'Added';
      var original = button.textContent;

      button.setAttribute('disabled', 'disabled');

      /* FormData with an Accept header only — setting Content-Type by hand
         breaks the multipart boundary. Sections are requested inline so the
         drawer repaints from the same response (gift-card-upsell-prompt
         .js:43-56). */
      var body = new FormData();
      body.append('id', variantId);
      body.append('quantity', 1);

      /* Carry the cart's delivery properties onto the add-on line. Bouquet
         lines get these from product-form.js:928-967 and cart-upgrade-button
         replays them across a swap (cart-notification.js:336-348) — an add-on
         ships in the same box on the same run, so a line without them arrives
         on the floor with no delivery date against it. Rendered from live cart
         state by the Liquid, so no round trip is needed before the add. */
      var props = {
        Suburb: this.dataset.propSuburb,
        Postcode: this.dataset.propPostcode,
        'Delivery Date': this.dataset.propDate,
        _ISODate: this.dataset.propIso
      };
      /* Never copy a date that has passed (or missed today's cutoff): the
         add-on goes in undated, and "Choose a new delivery date" dates it with
         the rest of the cart (assets/fb-cart-date-guard.js). */
      if (window.fbCartDateGuard && typeof window.fbCartDateGuard.addonDateProps === 'function') {
        props = window.fbCartDateGuard.addonDateProps(props);
      }
      Object.keys(props).forEach(function (key) {
        if (props[key]) body.append('properties[' + key + ']', props[key]);
      });

      body.append('sections', DRAWER_SECTIONS.join(','));
      body.append('sections_url', window.location.pathname);

      var self = this;
      fetch(addUrl + '.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: body
      })
        .then(function (res) {
          if (!res.ok) throw new Error('add-to-cart failed');
          return res.json();
        })
        .then(function (state) {
          /* /cart/add.js can answer 200 with a JSON error object carrying a
             `status` field, which a bare res.ok check lets through as success.
             product-form.js:1016-1021 gets this right; gift-card-upsell-prompt
             .js:57-60 does not. Follow the correct one. */
          if (state && state.status) throw new Error(state.description || 'add-to-cart rejected');

          /* Released here, not in a finally, and BEFORE the repaint below.
             renderContents replaces the rail, so anything scheduled after it
             runs against a detached element — but the flag is module scope and
             survives, which is what made leaving it set fatal rather than
             merely untidy: one successful add disabled every later add for the
             life of the page. The guard's job is to serialise concurrent adds,
             and by this point the response has landed and the cart is
             consistent, so the next add is safe to accept. */
          addInFlight = false;

          button.textContent = addedLabel;
          button.classList.add('is-added');

          /* Attribution only. 'Product Added' is emitted for us by the fetch
             interceptor in fb-posthog-commerce.js:427-454, which sees every
             POST to /cart/add — emitting our own add event here would
             double-count every add on the store. */
          capture('rail_slot_added', {
            surface: 'drawer',
            slot_position: slotPosition,
            won_at_rank: slotRank,
            location_id: orNull(addLocationId),
            add_ms: addStartedAt === null
              ? null
              : Math.round(window.performance.now() - addStartedAt),
            product_id: payload ? String(payload.product_id) : null,
            variant_id: String(variantId),
            handle: payload ? orNull(payload.handle) : null,
            product_type: payload ? orNull(payload.product_type) : null,
            slot_id: payload ? orNull(payload.slot_id) : null,
            price: payload ? dollars(payload.priceCents) : null,
            currency: orNull(self.dataset.currency)
          });

          var drawer = document.querySelector('cart-notification');
          if (drawer && typeof drawer.renderContents === 'function' && state && state.sections) {
            drawer.renderContents(state);
          } else {
            /* Rendered somewhere without the drawer element — degrade to the
               cart page rather than leaving the shopper with no feedback. */
            setTimeout(function () { window.location.href = cartUrl; }, 400);
            return;
          }

          /* The counts in the add response are not trusted anywhere else in the
             theme either; re-read and fan out to the three selector families
             (gift-card-upsell-prompt.js:82-92). */
          fetch(cartUrl + '.js', { headers: { Accept: 'application/json' } })
            .then(function (res) { return res.json(); })
            .then(function (cart) {
              if (!cart || typeof cart.item_count === 'undefined') return;
              document
                .querySelectorAll('.cart-count-bubble span[aria-hidden="true"], .cart-drawer-count, [data-cart-count]')
                .forEach(function (el) { el.textContent = cart.item_count; });
              if (typeof fbUpdateCartCountLabel === 'function') fbUpdateCartCountLabel(cart.item_count);
              document.dispatchEvent(new CustomEvent('cart:updated', { detail: { count: cart.item_count } }));
            })
            .catch(function () {});
        })
        .catch(function (err) {
          /* A failed add is silent to the shopper on this path, which also
             made it silent to us. It is the detector for the one class of
             regression that is never acceptable: a ranking change offering
             something the suppression rules should have hidden, so checkout
             bounces it. Any later stage that re-ranks slots is judged partly
             on this staying at zero. */
          capture('rail_add_failed', {
            surface: 'drawer',
            slot_position: slotPosition,
            won_at_rank: slotRank,
            location_id: orNull(addLocationId),
            handle: payload ? orNull(payload.handle) : null,
            variant_id: String(variantId),
            reason: err && err.message ? String(err.message).slice(0, 200) : null
          });

          /* Silent restore, matching the gift-card prompt. There is no shared
             error-display helper on this path; a sold-out mid-add is rare
             because availability is filtered in Liquid at render time. */
          addInFlight = false;
          button.removeAttribute('disabled');
          button.classList.remove('is-added');
          button.textContent = original;
        });
    }
  });

  /* The drawer dispatches no open or close event — open() and close() only
     toggle .active on <cart-notification> itself (cart-notification.js:590-611,
     with this.notification resolved at :537). Watch the attribute rather than
     adding a dispatch to a file that five other features already reach into. */
  function watchDrawer() {
    var drawer = document.querySelector('cart-notification');
    if (!drawer) return;

    new MutationObserver(function () {
      if (drawer.classList.contains('active')) {
        /* Minted BEFORE reportView so the impression carries it. The class
           toggles more than once per open in practice, so this is guarded by
           the same latch as the impression rather than by the attribute. */
        if (!viewedThisOpen) openId = mintOpenId();
        /* Opened: report the impression the page-load render could not. */
        var rail = drawer.querySelector('cart-addons-rail');
        if (rail && typeof rail.reportView === 'function') rail.reportView();
        /* And settle the arrows against the drawer as it is now shown. The
           off-canvas drawer keeps its layout while hidden, so the page-load
           pass usually got this right already; this is the cheap guarantee. */
        if (rail && typeof rail.updateNav === 'function') rail.updateNav();
      } else {
        /* Closed: the next open starts clean. Resetting the scroll position
           here as well as the two latches is what keeps them consistent — the
           position is preserved to survive a cart RE-RENDER, not to be
           remembered across a whole visit. Left set, it would also make the
           next open's restore scroll fire a synthetic scroll event that
           reported a depth the shopper had not reached. */
        viewedThisOpen = false;
        deepestPositionReported = 0;
        lastScrollLeft = 0;
        openId = null;
      }
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  /* The shopper changed suburb while the drawer was open, so the territory the
     slots were resolved against is stale. Fired by the delivery popup
     (delivery-suburb-popup.js:160, :1997, :2613, :3039) on `window`, same as
     fb-variant-location-filter.js listens for. */
  function watchDelivery() {
    window.addEventListener('deliveryInfoUpdated', function () {
      var rail = document.querySelector('cart-addons-rail');
      if (rail && typeof rail.resolveSlots === 'function') rail.resolveSlots();
    });
  }

  /* The rail's revenue guardrail. The design guide's stated rule is that
     checkout click-through from the drawer must not fall
     (docs/cart-upsell-design-guide.md:244), and until now no drawer
     checkout-click event existed anywhere in the theme, so every change to the
     rail shipped with the guardrail unmeasured. A longer or busier rail that
     costs checkout intent is a loss whatever it does to attach rate.

     Delegated on document rather than bound to #cartCheckoutButton directly:
     the button carries its own inline handler for the event-cart flow
     (snippets/cart-notification.liquid:206) which must not be disturbed, and a
     direct binding would be lost to any repaint that replaces it. Passive and
     non-capturing, so it cannot interfere with that handler or with
     navigation. */
  function watchCheckout() {
    document.addEventListener('click', function (evt) {
      var target = evt.target && evt.target.closest ? evt.target : null;
      if (!target) return;
      if (!target.closest('#cartCheckoutButton')) return;
      if (!drawerIsOpen()) return;

      var rail = document.querySelector('cart-addons-rail');
      capture('rail_checkout_clicked', {
        surface: 'drawer',
        /* Zero when the rail was suppressed for this cart, which is a real and
           distinct denominator from "the rail rendered and was ignored". */
        slot_count: rail && typeof rail.slots === 'function' ? rail.slots().length : 0
      });
    });
  }

  function init() {
    watchDrawer();
    watchDelivery();
    watchCheckout();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
